import { createContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Shape retornada por `get_my_access_context(_club_id)` (RPC SQL, migration
 * `20260710120000_rbac_granular_schema.sql`). 1 chamada por troca de
 * usuário/clube — evita 1 round-trip de `has_permission` por item de menu.
 */
export interface AccessContext {
  is_owner: boolean;
  roles: string[];
  permissions: string[];
  disabled_modules: string[];
}

export interface PermissionsContextValue {
  isOwner: boolean;
  roles: string[];
  /** true se o usuário tem a permissão (owner sempre true). */
  can: (permissionKey: string) => boolean;
  /** true se o módulo está habilitado no clube (ausência = habilitado). */
  moduleEnabled: (moduleKey: string) => boolean;
  isLoading: boolean;
}

const EMPTY_ACCESS: AccessContext = {
  is_owner: false,
  roles: [],
  permissions: [],
  disabled_modules: [],
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

/**
 * `get_my_access_context` retorna `jsonb` (tipado como `Json` pelo gerador
 * Supabase) — valida o shape em vez de `as unknown as AccessContext`
 * (IR-TS-001).
 */
function parseAccessContext(raw: unknown): AccessContext {
  if (!raw || typeof raw !== 'object') return EMPTY_ACCESS;
  const obj = raw as Record<string, unknown>;
  return {
    is_owner: obj.is_owner === true,
    roles: isStringArray(obj.roles) ? obj.roles : [],
    permissions: isStringArray(obj.permissions) ? obj.permissions : [],
    disabled_modules: isStringArray(obj.disabled_modules) ? obj.disabled_modules : [],
  };
}

export const PermissionsContext = createContext<PermissionsContextValue | null>(null);

/**
 * PermissionsProvider — RBAC granular por perfil (docs do plano em
 * /root/.claude/plans, seção "Front CRM"). Fica ao lado de `AuthBridge`
 * (não o estende: `AuthBridge` é o bridge congelado M-1) e consome
 * `user`/`club` via `useAuth()`. Consumido por `usePermissions()`
 * (hooks/usePermissions.ts) e indiretamente por `RequirePermission` e
 * `AppSidebar`.
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user, club } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['access-context', user?.id ?? null, club?.id ?? null],
    enabled: !!user && !!club,
    staleTime: 60_000,
    queryFn: async (): Promise<AccessContext> => {
      const { data, error } = await supabase.rpc('get_my_access_context', {
        _club_id: club!.id,
      });
      if (error) throw error;
      return parseAccessContext(data);
    },
  });

  const ctx = data ?? EMPTY_ACCESS;
  const permissionSet = useMemo(() => new Set(ctx.permissions), [ctx.permissions]);
  const disabledModuleSet = useMemo(() => new Set(ctx.disabled_modules), [ctx.disabled_modules]);

  const value = useMemo<PermissionsContextValue>(
    () => ({
      isOwner: ctx.is_owner,
      roles: ctx.roles,
      can: (permissionKey: string) => ctx.is_owner || permissionSet.has(permissionKey),
      moduleEnabled: (moduleKey: string) => !disabledModuleSet.has(moduleKey),
      isLoading: !!user && !!club && isLoading,
    }),
    [ctx.is_owner, ctx.roles, permissionSet, disabledModuleSet, isLoading, user, club],
  );

  return (
    <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
  );
}
